import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getToken } from "@/lib/session";

/**
 * Photo uploader. Calls onChange(photos) where photos = [{file_id, url}].
 * - Uses POST /api/uploads/photo per file.
 * - Renders thumbnails with delete (soft remove from local list only).
 */
export default function PhotoUploader({ photos = [], onChange, max = 6, label = "Add photos" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files);
    if (photos.length + arr.length > max) {
      toast.error(`Max ${max} photos`);
      return;
    }
    setUploading(true);
    const next = [...photos];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: not an image`);
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name}: over 8MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/uploads/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({ file_id: r.data.file_id, url: r.data.url, name: f.name });
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    onChange(next);
  };

  const removeAt = (i) => {
    const next = photos.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const fileToBlobUrl = (file_id) => {
    const token = getToken() || "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${file_id}?auth=${encodeURIComponent(token)}`;
  };

  return (
    <div data-testid="photo-uploader">
      <div className="flex flex-wrap gap-3">
        {photos.map((p, i) => (
          <div key={p.file_id} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#E2E8F0] bg-slate-100 group" data-testid={`photo-thumb-${i}`}>
            <img src={fileToBlobUrl(p.file_id)} alt={p.name || "uploaded"} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`photo-remove-${i}`}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="photo-add-btn"
            className="w-20 h-20 border-2 border-dashed border-[#E2E8F0] hover:border-[#C9A356] rounded-md grid place-items-center text-[#475569] hover:text-[#C9A356] transition"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        data-testid="photo-input"
        className="hidden"
      />
      <p className="text-xs text-[#475569] mt-2">{label} · max {max} · 8MB each · jpg/png/webp/heic</p>
    </div>
  );
}

/** Read-only photo gallery — used in admin/work-order detail to view uploaded photos. */
export function PhotoGallery({ photos = [] }) {
  const [open, setOpen] = useState(null);
  const fileToBlobUrl = (file_id) => {
    const token = getToken() || "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${file_id}?auth=${encodeURIComponent(token)}`;
  };
  if (!photos.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 mt-2" data-testid="photo-gallery">
        {photos.map((p, i) => (
          <button
            key={p.file_id}
            type="button"
            onClick={() => setOpen(p)}
            data-testid={`gallery-thumb-${i}`}
            className="w-16 h-16 rounded-md overflow-hidden border border-[#E2E8F0] hover:border-[#C9A356] transition"
          >
            <img src={fileToBlobUrl(p.file_id)} alt={p.name || ""} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      {open && (
        <div
          onClick={() => setOpen(null)}
          className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4 cursor-zoom-out"
          data-testid="gallery-lightbox"
        >
          <img src={fileToBlobUrl(open.file_id)} alt={open.name || ""} className="max-h-[90vh] max-w-full rounded-md shadow-2xl" />
        </div>
      )}
    </>
  );
}
